// valibot, not zod: this schema ships in the client bundle, where valibot's tree-shaking keeps it small.
import * as v from 'valibot'

import { CUE_BACKGROUND_COLORS } from '~shared/consts'
import { FirestoreTimestampSchema } from '~shared/core/firestore'
import type { FirestoreDocumentSnapshot } from '~shared/core/firestore'
import { generateSalt } from '~shared/core/generateSalt'
import { IsoDateSchema, parse, TOD_DISPLAY_FORMATS } from '~shared/timeutils'

// =============== CANONICAL ENUMS
//
// Object-const style, not TypeScript `enum`. Values are unchanged from the old
// enums, so stored data and wire payloads keep matching.

export const RundownStatus = {
  IMPORTED: 'imported',
  DRAFT: 'draft',
  AWAITING_DATA: 'awaiting-data',
  APPROVED: 'approved',
  FINALIZED: 'finalized',
  REJECTED: 'rejected',
  TEMPLATE: 'template',
} as const

export type RundownStatus = (typeof RundownStatus)[keyof typeof RundownStatus]

export type { TodDisplayFormat } from '~shared/timeutils'

// =============== TEXT VARIABLE LIMITS

export const TEXT_VARIABLES_LIMITS = {
  MAX_VARIABLES: 100,
  MAX_KEY_LENGTH: 50,
  MIN_KEY_LENGTH: 1,
  MAX_VALUE_LENGTH: 1000,
  KEY_PATTERN: /^[a-z0-9-]+$/,
} as const

// =============== LEAF SCHEMAS

export const RundownStatusSchema = v.picklist(Object.values(RundownStatus))

const TodDisplayFormatSchema = v.picklist(TOD_DISPLAY_FORMATS)

// The "Auto" option stores `''`. Reads map it to `null` (the locale default), so every
// consumer sees one `TodDisplayFormat | null`.
const StoredTodDisplayFormatSchema = v.nullish(
  v.pipe(
    v.union([TodDisplayFormatSchema, v.literal('')]),
    v.transform((format) => (format === '' ? null : format)),
  ),
  null,
)

// The start time of a document that has none: 09:00 today, in the runtime's own
// timezone. That is what `getRundownDefaults` has always produced.
function defaultStartTime(): Date {
  return parse('09:00:00', 'HH:mm:ss', new Date(), {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  })
}

// Accepts a stored Timestamp, or a `Date` because a default is a `Date` and valibot
// runs defaults through the wrapped schema.
const StartTimeSchema = v.union([FirestoreTimestampSchema, v.date()])

// =============== SETTINGS
//
// Every key has a default, and the default of a nested object is `{}`, which
// valibot runs back through the same schema. So a legacy document with partial
// settings (say, only `cueIndexStartFrom`) parses to a complete object, deep.

export const OutputMessageSchema = v.object({
  text: v.nullish(v.string(), ''),
  visible: v.nullish(v.boolean(), false),
  color: v.nullish(v.string(), '#ffffff'),
  bold: v.nullish(v.boolean(), false),
  underline: v.nullish(v.boolean(), false),
})

export type OutputMessage = v.InferOutput<typeof OutputMessageSchema>

export const RundownSettingsSchema = v.object({
  outputConfig: v.nullish(v.string(), ''),
  outputMessage: v.nullish(OutputMessageSchema, () => ({})),
  cueBackgroundColours: v.nullish(v.array(v.string()), () => [...CUE_BACKGROUND_COLORS]),
  currentCueHighlightColor: v.nullish(v.string(), ''),
  todDisplayFormat: StoredTodDisplayFormatSchema,
  cueIndexStartFrom: v.nullish(v.number(), 1),
  cueIndexPrefix: v.nullish(v.string(), ''),
  cueIndexPadding: v.nullish(v.number(), 1),
})

// =============== CUE ORDER
//
// The order tree of a rundown's cues. Groups carry `children`, every other item
// omits it. The schema is recursive, so it is annotated rather than inferred.

export type RundownCueOrderItem = {
  id: string
  children?: RundownCueOrderItem[]
}

export const RundownCueOrderItemSchema: v.GenericSchema<RundownCueOrderItem> = v.object({
  id: v.string(),
  children: v.optional(v.array(v.lazy(() => RundownCueOrderItemSchema))),
})

// Read side of the order tree. Reorder bodies were written unvalidated before,
// so a stored `children: null` is read as no children instead of failing the doc.
const StoredCueOrderItemSchema: v.GenericSchema<unknown, RundownCueOrderItem> = v.pipe(
  v.object({
    id: v.string(),
    children: v.nullish(v.array(v.lazy(() => StoredCueOrderItemSchema))),
  }),
  v.transform(({ id, children }) => (children == null ? { id } : { id, children })),
)

/**
 * `RundownCueOrderItem` as a read-only tree. Every `RundownCueOrderItem` is
 * assignable to it, and so is a valtio snapshot of one, which is readonly at
 * every depth. Read-only helpers take this type.
 */
export interface ReadonlyRundownCueOrderItem {
  readonly id: string
  readonly children?: readonly ReadonlyRundownCueOrderItem[]
}

// =============== TEXT VARIABLES
//
// Simple key/value string pairs. Writes are checked against these rules; reads
// of a stored map are not, so a legacy value keeps loading.

export const TextVariableKeySchema = v.pipe(
  v.string('Variable key must be a string'),
  v.minLength(
    TEXT_VARIABLES_LIMITS.MIN_KEY_LENGTH,
    `Variable key must be at least ${TEXT_VARIABLES_LIMITS.MIN_KEY_LENGTH} character`,
  ),
  v.maxLength(
    TEXT_VARIABLES_LIMITS.MAX_KEY_LENGTH,
    `Variable key cannot exceed ${TEXT_VARIABLES_LIMITS.MAX_KEY_LENGTH} characters`,
  ),
  v.regex(TEXT_VARIABLES_LIMITS.KEY_PATTERN, 'Variable key must contain only lowercase letters, numbers, and hyphens'),
)

export const TextVariableValueSchema = v.pipe(
  v.string('Variable value must be a string'),
  v.maxLength(
    TEXT_VARIABLES_LIMITS.MAX_VALUE_LENGTH,
    `Variable value cannot exceed ${TEXT_VARIABLES_LIMITS.MAX_VALUE_LENGTH} characters`,
  ),
)

// `v.record` alone accepts an array, so a plain-object check is intersected with it.
const PlainObjectSchema = v.custom<Record<string, unknown>>(
  (input) => typeof input === 'object' && input !== null && !Array.isArray(input),
  'Variables must be an object',
)

/** A whole text variables map, the body of a replace or merge. */
export const TextVariablesSchema = v.pipe(
  v.intersect([
    PlainObjectSchema,
    v.record(TextVariableKeySchema, TextVariableValueSchema, 'Variables must be an object'),
  ]),
  v.maxEntries(TEXT_VARIABLES_LIMITS.MAX_VARIABLES, `Maximum ${TEXT_VARIABLES_LIMITS.MAX_VARIABLES} variables allowed`),
)

/** The body that creates one text variable. Not strict: extra keys are ignored, as before. */
export const TextVariableCreateSchema = v.object({
  key: TextVariableKeySchema,
  value: TextVariableValueSchema,
})

/** The body that updates one text variable. */
export const TextVariableUpdateSchema = v.object({
  value: TextVariableValueSchema,
})

/** Collection of text variables in a rundown: simple key-value string pairs. */
export type TextVariables = Record<string, string>

// =============== FIRESTORE BOUNDARY
//
// The shape of `doc.data()` for a rundown document. Timestamps in, `Date`s out.
// `id` and the audit stamps (`createdAt`/`updatedAt`) come from the snapshot
// envelope, see {@link parseRundownSnapshot}. Every field is `nullish`-with-default
// so a legacy document that stores `null` (or omits the field) parses to a complete
// rundown instead of throwing.

export const RundownFirestoreSchema = v.object({
  name: v.nullish(v.string(), ''),
  teamId: v.nullish(v.string(), null),
  eventId: v.nullish(v.string(), null),
  runnerId: v.nullish(v.string(), null),
  prompterId: v.nullish(v.string(), null),
  // Order of the rundown's columns, by column id.
  columns: v.nullish(v.array(v.string()), () => []),
  // Order tree of the rundown's cues.
  cues: v.nullish(v.array(StoredCueOrderItemSchema), () => []),
  startTime: v.nullish(StartTimeSchema, defaultStartTime),
  endTime: v.nullish(FirestoreTimestampSchema, null),
  startCueId: v.nullish(v.string(), null),
  // Signs share links. Reads must not invent one (a random salt per read would
  // never verify), so a missing or null salt stays `undefined`, as it always has.
  // Signing then appends the string "undefined", which existing links were made
  // with. New docs get a real salt from `getRundownDefaults`.
  salt: v.pipe(
    v.nullish(v.string()),
    v.transform((salt) => salt ?? undefined),
  ),
  status: v.nullish(RundownStatusSchema, RundownStatus.DRAFT),
  timezone: v.nullish(v.string(), null),
  logo: v.nullish(v.string(), ''),
  settings: v.nullish(RundownSettingsSchema, () => ({})),
  textVariables: v.nullish(v.record(v.string(), v.string()), () => ({})),
  deletedAt: v.nullish(FirestoreTimestampSchema, null),
  archivedAt: v.nullish(FirestoreTimestampSchema, null),
})

/** The raw Firestore document shape accepted by {@link RundownFirestoreSchema}. */
export type RundownFirestore = v.InferInput<typeof RundownFirestoreSchema>

/** A parsed rundown's own fields, before the snapshot envelope adds id/audit. */
export type RundownFields = v.InferOutput<typeof RundownFirestoreSchema>

// =============== WIRE BOUNDARY
//
// The over-the-wire shape: ISO date strings and the system fields included.
// Parses to the same canonical domain `Rundown`.

export const RundownSerializedSchema = v.object({
  ...RundownFirestoreSchema.entries,
  id: v.string(),
  startTime: IsoDateSchema,
  endTime: v.nullish(IsoDateSchema, null),
  deletedAt: v.nullish(IsoDateSchema, null),
  archivedAt: v.nullish(IsoDateSchema, null),
  createdAt: IsoDateSchema,
  updatedAt: IsoDateSchema,
})

/** The raw serialized shape accepted by {@link RundownSerializedSchema}. */
export type RundownSerialized = v.InferInput<typeof RundownSerializedSchema>

// =============== WRITE BOUNDARY
//
// What a client may send to the rundown endpoints. An allowlist of wire fields, so
// system fields stay server-owned and a new field is not writable until listed
// here. Strict, so an unknown key is rejected rather than written.
//
// Settings are patched one key at a time through Firestore dot paths
// (`settings.cueIndexPrefix`), which is what the client sends. `settings.outputMessage`
// is the exception: Firestore replaces that whole map, so it is sent and checked whole.
//
// Nullable fields take `null` as the clear value. A key may be absent (no change),
// but `undefined` is not a value a client sends, so there is no `nullish` here.

const OutputMessageWriteSchema = v.strictObject({
  text: v.string(),
  visible: v.boolean(),
  color: v.string(),
  bold: v.boolean(),
  underline: v.boolean(),
})

export const RundownPatchSchema = v.strictObject({
  name: v.optional(v.string()),
  status: v.optional(RundownStatusSchema),
  startTime: v.optional(IsoDateSchema),
  endTime: v.optional(v.nullable(IsoDateSchema)),
  startCueId: v.optional(v.nullable(v.string())),
  archivedAt: v.optional(v.nullable(IsoDateSchema)),
  timezone: v.optional(v.nullable(v.string())),
  logo: v.optional(v.string()),
  eventId: v.optional(v.nullable(v.string())),
  'settings.outputConfig': v.optional(v.string()),
  'settings.outputMessage': v.optional(OutputMessageWriteSchema),
  'settings.cueBackgroundColours': v.optional(v.array(v.string())),
  'settings.currentCueHighlightColor': v.optional(v.string()),
  // The write side still accepts `''`: the "Auto" option sends it. Reads normalize it.
  'settings.todDisplayFormat': v.optional(v.nullable(v.union([TodDisplayFormatSchema, v.literal('')]))),
  'settings.cueIndexStartFrom': v.optional(v.number()),
  'settings.cueIndexPrefix': v.optional(v.string()),
  'settings.cueIndexPadding': v.optional(v.number()),
})

export type RundownPatch = v.InferOutput<typeof RundownPatchSchema>

/** What a client may seed a new rundown with. The rest comes from the defaults. */
export const RundownCreateSchema = v.strictObject({
  name: v.optional(v.string()),
  teamId: v.nullish(v.string()),
  eventId: v.nullish(v.string()),
  timezone: v.nullish(v.string()),
  startTime: v.optional(IsoDateSchema),
})

export type RundownCreate = v.InferOutput<typeof RundownCreateSchema>

/** Body of `PATCH /rundowns/:id/reorderCues`: the whole new cue order tree. */
export const ReorderCuesBodySchema = v.array(RundownCueOrderItemSchema)

/** Body of `PATCH /rundowns/:id/reorderColumns`: the whole new column order. */
export const ReorderColumnsBodySchema = v.array(v.string())

// =============== CANONICAL DOMAIN TYPE

/**
 * The canonical Rundown.
 *
 * Fully parsed, `Date`-typed, with system fields.
 */
export type Rundown = RundownFields & {
  id: string
  createdAt: Date
  updatedAt: Date
}

// =============== HELPERS

/**
 * The schema's default rundown fields, with the Date defaults as `Date`s. Every
 * field has a default, so the result is complete. The single source of defaults.
 */
export function getRundownDefaults(): RundownFields {
  return { ...v.parse(RundownFirestoreSchema, {}), salt: generateSalt(16) }
}

// =============== PARSERS

// The whole document body, audit stamps included, so one parse reports every issue.
const RundownDocumentSchema = v.object({
  ...RundownFirestoreSchema.entries,
  createdAt: v.nullish(FirestoreTimestampSchema),
  updatedAt: v.nullish(FirestoreTimestampSchema),
})

/**
 * Parse a Firestore `DocumentSnapshot` into a canonical rundown.
 *
 * The document body is validated by {@link RundownFirestoreSchema}; `id` comes from
 * the snapshot and the audit stamps prefer an explicit document field, then the
 * snapshot metadata, then the epoch (`new Date(0)`, matching the server-side
 * `converters.fromSnapshot`).
 *
 * @throws {Error} naming the rundown and every issue when the body does not match.
 */
export function parseRundownSnapshot(snapshot: FirestoreDocumentSnapshot): Rundown {
  const result = v.safeParse(RundownDocumentSchema, snapshot.data() ?? {})
  if (!result.success) throw new Error(`Invalid rundown document ${snapshot.id}:\n${v.summarize(result.issues)}`)
  const { createdAt, updatedAt, ...fields } = result.output

  return {
    ...fields,
    id: snapshot.id,
    createdAt: createdAt ?? snapshot.createTime?.toDate() ?? new Date(0),
    updatedAt: updatedAt ?? snapshot.updateTime?.toDate() ?? new Date(0),
  }
}

/**
 * Parse a serialized rundown (ISO date strings) into a canonical rundown.
 *
 * @throws {ValiError} when the input does not match.
 */
export function parseRundownSerialized(input: unknown): Rundown {
  return v.parse(RundownSerializedSchema, input)
}

/**
 * Serialize a canonical rundown into the over-the-wire shape.
 *
 * `Date` does not survive JSON, so every date field is emitted as an ISO string
 * (or `null`). The result round-trips through {@link parseRundownSerialized}.
 */
export function serializeRundown(rundown: Rundown): RundownSerialized {
  return {
    ...rundown,
    startTime: rundown.startTime.toISOString(),
    endTime: rundown.endTime?.toISOString() ?? null,
    deletedAt: rundown.deletedAt?.toISOString() ?? null,
    archivedAt: rundown.archivedAt?.toISOString() ?? null,
    createdAt: rundown.createdAt.toISOString(),
    updatedAt: rundown.updatedAt.toISOString(),
  }
}
